import { useRef, useEffect, useState, useCallback } from 'react';
import type { Annotation } from '../../api/client';
import type { AnnotationTool } from './AnnotationToolbar';
import { X } from 'lucide-react';

interface AnnotationCanvasProps {
  annotations: Annotation[];
  onAnnotationCreate: (annotation: {
    annotationType: Annotation['AnnotationType'];
    coordinates: any;
    pageNumber?: number | null;
    content?: string;
    color?: string;
  }) => void;
  onAnnotationDelete: (annotationId: string) => void;
  activeTool: AnnotationTool;
  activeColor: string;
  pageNumber?: number | null;
  width: number;
  height: number;
  readOnly?: boolean;
  rectangleFill?: boolean;
}

interface Point {
  x: number;
  y: number;
}

export default function AnnotationCanvas({
  annotations,
  onAnnotationCreate,
  onAnnotationDelete,
  activeTool,
  activeColor,
  pageNumber,
  width,
  height,
  readOnly = false,
  rectangleFill = false,
}: AnnotationCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<Point | null>(null);
  const [freehandPoints, setFreehandPoints] = useState<Point[]>([]);
  const [selectedAnnotation, setSelectedAnnotation] = useState<string | null>(null);
  const [textInput, setTextInput] = useState<{ x: number; y: number; visible: boolean }>({ x: 0, y: 0, visible: false });
  const [textValue, setTextValue] = useState('');

  // Filter annotations for this page
  const pageAnnotations = annotations.filter(
    (a) => a.PageNumber == null || pageNumber == null || Number(a.PageNumber) === Number(pageNumber)
  );

  // Draw all annotations on the canvas
  const drawAnnotations = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, width, height);

    for (const ann of pageAnnotations) {
      let coords: any;
      try {
        coords = typeof ann.Coordinates === 'string' ? JSON.parse(ann.Coordinates) : ann.Coordinates;
      } catch {
        continue;
      }

      const color = ann.Color || '#FFEB3B';
      const isSelected = selectedAnnotation === ann.Id;

      switch (ann.AnnotationType) {
        case 'highlight': {
          ctx.fillStyle = color + '40'; // 25% opacity
          ctx.fillRect(coords.x, coords.y, coords.width, coords.height);
          if (isSelected) {
            ctx.strokeStyle = color;
            ctx.lineWidth = 2;
            ctx.setLineDash([4, 4]);
            ctx.strokeRect(coords.x, coords.y, coords.width, coords.height);
            ctx.setLineDash([]);
          }
          break;
        }
        case 'rectangle': {
          const isFilled = coords.filled === true;
          if (isFilled) {
            ctx.fillStyle = color + '66'; // 40% opacity fill
            ctx.fillRect(coords.x, coords.y, coords.width, coords.height);
          }
          ctx.strokeStyle = color;
          ctx.lineWidth = 2;
          ctx.strokeRect(coords.x, coords.y, coords.width, coords.height);
          if (isSelected) {
            ctx.setLineDash([4, 4]);
            ctx.strokeRect(coords.x - 2, coords.y - 2, coords.width + 4, coords.height + 4);
            ctx.setLineDash([]);
          }
          break;
        }
        case 'freehand': {
          if (!coords.points || coords.points.length < 2) break;
          ctx.strokeStyle = color;
          ctx.lineWidth = 2;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.beginPath();
          ctx.moveTo(coords.points[0].x, coords.points[0].y);
          for (let i = 1; i < coords.points.length; i++) {
            ctx.lineTo(coords.points[i].x, coords.points[i].y);
          }
          ctx.stroke();
          break;
        }
        case 'text': {
          // Draw text bubble with high-contrast background
          const text = ann.Content || '';
          const isPrivate = ann.Visibility === 'private';
          const prefix = isPrivate ? '🔒 ' : '';
          ctx.font = 'bold 13px Inter, system-ui, sans-serif';
          const metrics = ctx.measureText(prefix + text);
          const textWidth = Math.min(metrics.width + 20, 260);
          const textHeight = 32;
          const rx = coords.x;
          const ry = coords.y;
          // Drop shadow
          ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
          ctx.beginPath();
          ctx.roundRect(rx + 2, ry + 2, textWidth, textHeight, 6);
          ctx.fill();
          // Solid background
          ctx.fillStyle = color + 'E6'; // 90% opacity
          ctx.strokeStyle = color;
          ctx.lineWidth = 2;
          if (isPrivate) {
            ctx.setLineDash([4, 3]);
          }
          ctx.beginPath();
          ctx.roundRect(rx, ry, textWidth, textHeight, 6);
          ctx.fill();
          ctx.stroke();
          ctx.setLineDash([]);
          // Text with contrast
          ctx.fillStyle = '#111827';
          ctx.fillText(prefix + text, rx + 10, ry + 21, textWidth - 20);
          // Pin dot
          ctx.fillStyle = color;
          ctx.strokeStyle = '#111827';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(rx, ry + textHeight / 2, 5, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          break;
        }
        case 'arrow': {
          ctx.strokeStyle = color;
          ctx.fillStyle = color;
          ctx.lineWidth = 2;
          // Line
          ctx.beginPath();
          ctx.moveTo(coords.x1, coords.y1);
          ctx.lineTo(coords.x2, coords.y2);
          ctx.stroke();
          // Arrowhead
          const angle = Math.atan2(coords.y2 - coords.y1, coords.x2 - coords.x1);
          const headLen = 12;
          ctx.beginPath();
          ctx.moveTo(coords.x2, coords.y2);
          ctx.lineTo(
            coords.x2 - headLen * Math.cos(angle - Math.PI / 6),
            coords.y2 - headLen * Math.sin(angle - Math.PI / 6)
          );
          ctx.lineTo(
            coords.x2 - headLen * Math.cos(angle + Math.PI / 6),
            coords.y2 - headLen * Math.sin(angle + Math.PI / 6)
          );
          ctx.closePath();
          ctx.fill();
          break;
        }
        case 'redaction': {
          // Solid black opaque redaction box
          ctx.fillStyle = '#000000';
          ctx.fillRect(coords.x, coords.y, coords.width, coords.height);
          // "REDACTED" label
          ctx.fillStyle = '#FFFFFF';
          const fontSize = Math.min(14, coords.height * 0.5, coords.width * 0.08);
          ctx.font = `bold ${fontSize}px Inter, system-ui, sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          if (coords.width > 50 && coords.height > 16) {
            ctx.fillText('REDACTED', coords.x + coords.width / 2, coords.y + coords.height / 2, coords.width - 8);
          }
          ctx.textAlign = 'start';
          ctx.textBaseline = 'alphabetic';
          // Red border
          ctx.strokeStyle = '#991B1B';
          ctx.lineWidth = 2;
          ctx.strokeRect(coords.x, coords.y, coords.width, coords.height);
          if (isSelected) {
            ctx.setLineDash([4, 4]);
            ctx.strokeStyle = '#EF4444';
            ctx.strokeRect(coords.x - 3, coords.y - 3, coords.width + 6, coords.height + 6);
            ctx.setLineDash([]);
          }
          break;
        }
      }
    }

    // Draw in-progress shapes
    if (isDrawing && startPoint) {
      const ctx2 = ctx;
      ctx2.strokeStyle = activeColor;
      ctx2.lineWidth = 2;
      ctx2.setLineDash([6, 3]);

      if (activeTool === 'freehand' && freehandPoints.length > 1) {
        ctx2.beginPath();
        ctx2.moveTo(freehandPoints[0].x, freehandPoints[0].y);
        for (let i = 1; i < freehandPoints.length; i++) {
          ctx2.lineTo(freehandPoints[i].x, freehandPoints[i].y);
        }
        ctx2.stroke();
      }
      ctx2.setLineDash([]);
    }
  }, [pageAnnotations, selectedAnnotation, isDrawing, startPoint, freehandPoints, activeTool, activeColor, width, height]);

  useEffect(() => {
    drawAnnotations();
  }, [drawAnnotations]);

  const getCanvasPoint = (e: React.MouseEvent): Point => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * width,
      y: ((e.clientY - rect.top) / rect.height) * height,
    };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (readOnly || !activeTool) {
      // Click to select/deselect annotation
      const pt = getCanvasPoint(e);
      const clicked = findAnnotationAt(pt);
      setSelectedAnnotation(clicked?.Id || null);
      return;
    }

    const pt = getCanvasPoint(e);

    if (activeTool === 'text') {
      setTextInput({ x: pt.x, y: pt.y, visible: true });
      setTextValue('');
      return;
    }

    setIsDrawing(true);
    setStartPoint(pt);
    if (activeTool === 'freehand') {
      setFreehandPoints([pt]);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDrawing || !startPoint || readOnly) return;
    const pt = getCanvasPoint(e);

    if (activeTool === 'freehand') {
      setFreehandPoints((prev) => [...prev, pt]);
    } else {
      // Draw preview
      drawAnnotations();
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      ctx.strokeStyle = activeColor;
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);

      if (activeTool === 'highlight' || activeTool === 'rectangle') {
        const w = pt.x - startPoint.x;
        const h = pt.y - startPoint.y;
        if (activeTool === 'highlight') {
          ctx.fillStyle = activeColor + '40';
          ctx.fillRect(startPoint.x, startPoint.y, w, h);
        } else if (activeTool === 'rectangle' && rectangleFill) {
          ctx.fillStyle = activeColor + '66';
          ctx.fillRect(startPoint.x, startPoint.y, w, h);
        }
        ctx.strokeRect(startPoint.x, startPoint.y, w, h);
      } else if (activeTool === 'redaction') {
        const w = pt.x - startPoint.x;
        const h = pt.y - startPoint.y;
        ctx.fillStyle = '#000000CC';
        ctx.fillRect(startPoint.x, startPoint.y, w, h);
        ctx.strokeStyle = '#991B1B';
        ctx.strokeRect(startPoint.x, startPoint.y, w, h);
      } else if (activeTool === 'arrow') {
        ctx.beginPath();
        ctx.moveTo(startPoint.x, startPoint.y);
        ctx.lineTo(pt.x, pt.y);
        ctx.stroke();
      }
      ctx.setLineDash([]);
    }
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!isDrawing || !startPoint || readOnly || !activeTool) {
      setIsDrawing(false);
      return;
    }

    const pt = getCanvasPoint(e);

    if (activeTool === 'highlight' || activeTool === 'rectangle') {
      const w = pt.x - startPoint.x;
      const h = pt.y - startPoint.y;
      if (Math.abs(w) > 5 && Math.abs(h) > 5) {
        onAnnotationCreate({
          annotationType: activeTool,
          coordinates: {
            x: Math.min(startPoint.x, pt.x),
            y: Math.min(startPoint.y, pt.y),
            width: Math.abs(w),
            height: Math.abs(h),
            ...(activeTool === 'rectangle' && { filled: rectangleFill }),
          },
          pageNumber,
          color: activeColor,
        });
      }
    } else if (activeTool === 'redaction') {
      const w = pt.x - startPoint.x;
      const h = pt.y - startPoint.y;
      if (Math.abs(w) > 5 && Math.abs(h) > 5) {
        onAnnotationCreate({
          annotationType: 'redaction',
          coordinates: {
            x: Math.min(startPoint.x, pt.x),
            y: Math.min(startPoint.y, pt.y),
            width: Math.abs(w),
            height: Math.abs(h),
          },
          pageNumber,
          color: '#000000',
        });
      }
    } else if (activeTool === 'arrow') {
      const dist = Math.sqrt((pt.x - startPoint.x) ** 2 + (pt.y - startPoint.y) ** 2);
      if (dist > 10) {
        onAnnotationCreate({
          annotationType: 'arrow',
          coordinates: { x1: startPoint.x, y1: startPoint.y, x2: pt.x, y2: pt.y },
          pageNumber,
          color: activeColor,
        });
      }
    } else if (activeTool === 'freehand' && freehandPoints.length > 2) {
      onAnnotationCreate({
        annotationType: 'freehand',
        coordinates: { points: freehandPoints },
        pageNumber,
        color: activeColor,
      });
    }

    setIsDrawing(false);
    setStartPoint(null);
    setFreehandPoints([]);
  };

  const handleTextSubmit = () => {
    if (textValue.trim()) {
      onAnnotationCreate({
        annotationType: 'text',
        coordinates: { x: textInput.x, y: textInput.y },
        content: textValue.trim(),
        pageNumber,
        color: activeColor,
      });
    }
    setTextInput({ x: 0, y: 0, visible: false });
    setTextValue('');
  };

  const findAnnotationAt = (pt: Point): Annotation | null => {
    // Reverse order so top-most annotation is found first
    for (let i = pageAnnotations.length - 1; i >= 0; i--) {
      const ann = pageAnnotations[i];
      let coords: any;
      try {
        coords = typeof ann.Coordinates === 'string' ? JSON.parse(ann.Coordinates) : ann.Coordinates;
      } catch {
        continue;
      }

      if (ann.AnnotationType === 'highlight' || ann.AnnotationType === 'rectangle' || ann.AnnotationType === 'redaction') {
        if (pt.x >= coords.x && pt.x <= coords.x + coords.width && pt.y >= coords.y && pt.y <= coords.y + coords.height) {
          return ann;
        }
      } else if (ann.AnnotationType === 'text') {
        if (pt.x >= coords.x - 10 && pt.x <= coords.x + 210 && pt.y >= coords.y - 5 && pt.y <= coords.y + 35) {
          return ann;
        }
      }
    }
    return null;
  };

  return (
    <div className="absolute inset-0 z-10" style={{ pointerEvents: activeTool || !readOnly ? 'auto' : 'none' }}>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className="absolute inset-0 w-full h-full"
        style={{ cursor: activeTool ? 'crosshair' : selectedAnnotation ? 'pointer' : 'default' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => { setIsDrawing(false); setStartPoint(null); setFreehandPoints([]); }}
      />

      {/* Text input popup */}
      {textInput.visible && (
        <div
          className="absolute z-50"
          style={{
            left: `${(textInput.x / width) * 100}%`,
            top: `${(textInput.y / height) * 100}%`,
          }}
        >
          <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-2 flex gap-1">
            <input
              type="text"
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleTextSubmit(); if (e.key === 'Escape') setTextInput({ x: 0, y: 0, visible: false }); }}
              placeholder="Add note..."
              className="text-sm border border-gray-200 rounded px-2 py-1 w-48 focus:outline-none focus:ring-1 focus:ring-primary-500"
              autoFocus
            />
            <button onClick={handleTextSubmit} className="text-sm bg-primary-600 text-white px-2 py-1 rounded hover:bg-primary-700">
              Add
            </button>
          </div>
        </div>
      )}

      {/* Selected annotation delete button */}
      {selectedAnnotation && !readOnly && (() => {
        const ann = pageAnnotations.find((a) => a.Id === selectedAnnotation);
        if (!ann) return null;
        let coords: any;
        try {
          coords = typeof ann.Coordinates === 'string' ? JSON.parse(ann.Coordinates) : ann.Coordinates;
        } catch { return null; }
        const px = ann.AnnotationType === 'arrow' ? coords.x2 : (coords.x + (coords.width || 0));
        const py = ann.AnnotationType === 'arrow' ? coords.y2 : coords.y;
        return (
          <button
            className="absolute z-50 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600 transition-colors"
            style={{
              left: `${(px / width) * 100}%`,
              top: `${(py / height) * 100}%`,
              transform: 'translate(-50%, -50%)',
            }}
            onClick={(e) => { e.stopPropagation(); onAnnotationDelete(selectedAnnotation); setSelectedAnnotation(null); }}
            title="Delete annotation"
          >
            <X className="w-3 h-3" />
          </button>
        );
      })()}
    </div>
  );
}
